/**
 * Browser page translation (Chrome/Edge "Translate this page", Google Translate extension) replaces our text nodes with
 * <font> elements. React still holds the original nodes, so the next update that inserts or removes something next to
 * them throws "NotFoundError: Failed to execute 'insertBefore'/'removeChild' on 'Node'" and the page shows the error
 * card (bug reported 2026-10-28: onboarding "继续" in a machine-translated tab). This is a known React limitation
 * (facebook/react#11538); the usual remedy is to make these two DOM methods tolerant:
 *
 *   - insertBefore with a reference node that is no longer a child: insert before the child of this parent that now
 *     contains it (the translation wrapper), or append when it is gone entirely;
 *   - removeChild of a node that is no longer a child: remove it from where it is now if that is inside this parent,
 *     otherwise do nothing (it is already out of the page).
 *
 * Normal pages never take these branches. Translated text may stay stale until the next full render, which is why the
 * app also offers its own Chinese interface (TranslationNotice).
 */
let installed = false;

export function installTranslationGuard(proto: Node = typeof Node === 'function' ? Node.prototype : (undefined as unknown as Node)) {
  if (!proto || installed) return;
  installed = true;
  const originalRemove = proto.removeChild;
  const originalInsert = proto.insertBefore;

  proto.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode === this) return originalRemove.call(this, child) as T;
    if (child.parentNode && this.contains(child)) return originalRemove.call(child.parentNode, child) as T;
    return child;
  } as typeof proto.removeChild;

  proto.insertBefore = function insertBefore<T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (!ref || ref.parentNode === this) return originalInsert.call(this, node, ref) as T;
    let anchor: Node | null = ref;
    while (anchor && anchor.parentNode !== this) anchor = anchor.parentNode;
    return originalInsert.call(this, node, anchor) as T;
  } as typeof proto.insertBefore;
}

/** Chrome and the Google Translate widget mark a translated page with these classes on <html>. */
export function isPageTranslated(doc: Document = document): boolean {
  const c = doc.documentElement.classList;
  return c.contains('translated-ltr') || c.contains('translated-rtl');
}
